import { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import unidecode from 'unidecode';
import i18n from 'i18n';
import { classNames } from '../utils';
import { ReactComponent as IconPassword } from 'assets/iconButtons/eye.svg';
import { ReactComponent as IconPasswordOff } from 'assets/iconButtons/eye-off.svg';
import { ReactComponent as IconError } from 'assets/iconButtons/warning.svg';
import { MAX_MESSAGE_LENGTH } from 'components/common/constants';
import { sanitizeMessageText } from 'services/utils';
import './Input.scss';

// Quill toolbar configuration
const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const Input = ({
  type = 'text',
  name,
  error,
  isApiError,
  value,
  setValue,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  label,
  placeholder,
  subText,
  symbolsCountText,
  className,
  Icon,
  initialStatus,
  isDisabled,
  autoComplete = 'on',
  isRequired,
  autoFocus = false,
  max,
  rows,
  setCountryCode,
  excludeCountries = [],
  inputRef
}) => {
  const [inputType, setInputType] = useState(type);
  const [isFocused, setIsFocused] = useState(false);
  const [textTruncatedWarning, setTextTruncatedWarning] = useState(false);
  const quillRef = useRef(null);
  const lastValidContent = useRef(null);

  // Clear truncation warning after 3 seconds
  useEffect(() => {
    if (textTruncatedWarning) {
      const timer = setTimeout(() => {
        setTextTruncatedWarning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [textTruncatedWarning]);

  const getQuillTextLength = (value) => {
    const quillInstance = quillRef.current?.getEditor();

    return quillInstance?.getLength() - 1;
  };

  // Quill specific handlers
  const handleQuillChange = (content, delta, source, editor) => {
    // For Quill, we work directly with HTML strings
    const text = editor.getText();
    const html = editor.getHTML();

    // Remove trailing newline that Quill adds
    const actualTextLength = text.length - 1;

    // Check if we're at or exceeding the limit
    if (max && actualTextLength > max) {
      const quillInstance = quillRef.current?.getEditor();
      if (!quillInstance) return;

      // Check if this was a paste operation (multiple characters added at once)
      const isPaste =
        source === 'user' &&
        delta?.ops?.some((op) => op.insert && typeof op.insert === 'string' && op.insert.length > 1);

      if (isPaste) {
        // For paste operations: get all current content and truncate properly
        const allOps = quillInstance.getContents().ops;
        let textLength = 0;
        const newOps = [];

        // Build new content respecting the max limit
        for (const op of allOps) {
          if (textLength >= max) break;

          if (op.insert) {
            const insertText = typeof op.insert === 'string' ? op.insert : '\n';
            const remainingSpace = max - textLength;

            if (remainingSpace <= 0) break;

            if (insertText.length <= remainingSpace) {
              newOps.push(op);
              textLength += insertText.length;
            } else {
              // Truncate this operation
              newOps.push({
                ...op,
                insert: insertText.substring(0, remainingSpace)
              });
              textLength = max;
              break;
            }
          } else {
            newOps.push(op);
          }
        }

        // Set the truncated content
        quillInstance.setContents({ ops: newOps });

        // Show truncation warning only for paste
        setTextTruncatedWarning(true);

        // Update with truncated content
        onChange(quillInstance.root.innerHTML);
        lastValidContent.current = quillInstance.getContents();
      } else {
        // For regular typing: restore last valid content
        if (lastValidContent.current) {
          quillInstance.setContents(lastValidContent.current);
        } else {
          // Fallback: remove the last inserted characters
          const currentLength = quillInstance.getLength();
          const excessChars = currentLength - max - 1; // -1 for Quill's trailing newline
          if (excessChars > 0) {
            const selection = quillInstance.getSelection();
            if (selection) {
              // Delete the excess characters before cursor position
              quillInstance.deleteText(selection.index - excessChars, excessChars);
            }
          }
        }
      }

      return;
    }

    // Save valid content for future reference
    const quillInstance = quillRef.current?.getEditor();
    if (quillInstance) {
      lastValidContent.current = quillInstance.getContents();
    }

    // Clear warning if text is within limit
    if (textTruncatedWarning) {
      setTextTruncatedWarning(false);
    }

    // Pass HTML string directly to onChange
    onChange(html);
  };

  const handleQuillFocus = () => {
    setIsFocused(true);
    onFocus();
  };

  const handleQuillBlur = (e) => {
    setIsFocused(false);
    onBlur(e);
  };

  const handleQuillFix = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      // Quill adds a trailing newline by default, we need to remove it
      let currentText = editor.getText();

      // Remove trailing newline that Quill adds automatically
      if (currentText.endsWith('\n')) {
        currentText = currentText.slice(0, -1);
      }

      // Define skip ranges for unidecode (same as in old RTE)
      const skipRanges = [
        [0x41, 0x7a], // English letters (A-Z, a-z)
        [0x24, 0x24], // Dollar sign ($)
        [0xa3, 0xa3] // Pound sign (£)
      ];

      // First apply unidecode to convert cyrillic and other non-latin chars
      const unidecodedText = unidecode(currentText, { skipRanges });

      // Then apply additional sanitization for special characters
      const sanitizedText = sanitizeMessageText(unidecodedText);

      // Delete all content and insert sanitized text
      editor.deleteText(0, editor.getLength());
      editor.insertText(0, sanitizedText);

      // Trigger onChange with the new HTML
      const html = editor.root.innerHTML;
      onChange(html);
    }
  };

  // Quill modules configuration with custom toolbar
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link']
          // ['fix'] // Custom button placeholder
        ],
        handlers: {
          fix: handleQuillFix
        }
      }
    }),
    []
  );

  // Initialize custom Fix button in Quill toolbar
  useEffect(() => {
    if (type === 'text-editor-quill' && quillRef.current) {
      const toolbar = document.querySelector('.quill-editor-container .ql-toolbar');
      if (toolbar) {
        const fixButton = toolbar.querySelector('.ql-fix');
        if (fixButton) {
          fixButton.innerHTML = 'Fix';
          fixButton.title = 'Fix problematic characters from ChatGPT and other sources';
          fixButton.classList.add('ql-fix-button');
        }
      }
    }
  }, [type, value]);

  const toggleShowingPassword = () => {
    setInputType(inputType === 'password' ? 'text' : 'password');
  };

  const inputChange = (e) => {
    if (name === 'comment' && e.target.value.startsWith(' ') && e.target.value.trim() === '') {
      onChange({ ...e, target: { ...e.target, value: '' } });
    } else {
      onChange(e);
    }
  };

  const handleBlur = (e) => onBlur(e);

  const handleFocus = () => onFocus();

  const handleLinkClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
    }
  };

  const handleEnterPress = (e) => {
    if (e.key === 'Enter' && isDisabled) {
      e.preventDefault();
    }
  };

  const inputClasses = classNames({
    'input-wrapper': true,
    error: !!error || !!isApiError,
    hit: !error && !isApiError && !!initialStatus,
    active: value?.length,
    disabled: isDisabled,
    icon: Icon || type === 'password' || error,
    [className]: true
  });

  const renderIcons = () => {
    if (Icon) {
      return <Icon className={'input-icon'} />;
    }

    if (type === 'password') {
      return (
        <div
          role={'button'}
          tabIndex={0}
          onKeyDown={toggleShowingPassword}
          onClick={toggleShowingPassword}
          className={`input-icon eye ${error ? 'error' : ''}`}
        >
          {inputType === 'text' ? <IconPassword /> : <IconPasswordOff />}
          {error && <IconError />}
        </div>
      );
    }

    return error && <IconError className={'input-icon'} />;
  };

  return type === 'phone' ? (
    <div className={inputClasses}>
      <PhoneInput
        /* eslint-disable-next-line max-len */
        containerClass={`custom-react-tel-input ${(error || isApiError) && 'invalid-tel-number'} ${
          !error && !isApiError && !!initialStatus && 'hit-tel-number'
        }`}
        country={process.env.REACT_APP_DEFAULT_COUNTRY_CODE.toLowerCase()}
        excludeCountries={excludeCountries}
        specialLabel={label + (isRequired ? '*' : '')}
        inputProps={{ name, required: isRequired, autoFocus }}
        copyNumbersOnly={false}
        value={value}
        onBlur={onBlur}
        onChange={(phoneNumber, countryData, e, formattedValue) => {
          if (e.type === 'click') {
            setValue(name, formattedValue);
          } else {
            onChange(e);
          }
          setCountryCode(countryData?.countryCode);
        }}
      />
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`input-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
          {symbolsCountText && <p className={'input-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  ) : type === 'textarea' ? (
    <div className={inputClasses}>
      {label && <label htmlFor={name}>{label + (isRequired ? '*' : '')}</label>}
      <textarea
        id={name}
        name={name}
        value={value || ''}
        onChange={inputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={isDisabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={max}
        rows={rows}
        className={'input'}
      />
      {renderIcons()}
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`input-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
          {symbolsCountText && <p className={'input-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  ) : type === 'text-editor-quill' ? (
    <div
      className={'text-editor-wrapper'}
      onClick={handleLinkClick}
      onKeyDown={handleEnterPress}
      tabIndex={0}
      role={isDisabled ? 'button' : ''}
    >
      <label className={`text-editor-label ${inputClasses}`} htmlFor={'message'}>
        {label}
      </label>
      <div
        className={`quill-editor-container ${isDisabled ? 'disabled' : ''} ${value && value.length > 0 && value !== '<p><br></p>' ? 'active' : ''} ${inputClasses}`}
      >
        <ReactQuill
          id={name}
          ref={quillRef}
          theme='snow'
          value={typeof value === 'string' ? value : ''}
          onChange={handleQuillChange}
          onFocus={handleQuillFocus}
          onBlur={handleQuillBlur}
          placeholder={placeholder}
          readOnly={isDisabled}
          modules={quillModules}
          formats={quillFormats}
          maxLength={max}
        />
      </div>
      {(error || subText || symbolsCountText || textTruncatedWarning) && (
        <div
          className={`text-editor-under-text-wrapper ${!error && !subText && !textTruncatedWarning && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText || textTruncatedWarning) && (
            <p className={`text-editor-sub-text ${error || textTruncatedWarning ? 'text-editor-sub-text-error' : ''}`}>
              {error || (textTruncatedWarning ? 'Text was truncated to maximum length' : subText)}
            </p>
          )}
          {symbolsCountText && (
            <p className={'text-editor-symbols-count-text'}>
              {i18n.getMessage('messages.form.input.message.symbolsLeft', {
                amount: MAX_MESSAGE_LENGTH - getQuillTextLength()
              })}
            </p>
          )}
        </div>
      )}
    </div>
  ) : (
    <div className={inputClasses}>
      {label && <label htmlFor={name}>{label + (isRequired ? '*' : '')}</label>}
      <input
        id={name}
        ref={inputRef}
        name={name}
        type={inputType}
        value={value || ''}
        onChange={inputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={isDisabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={max}
        className={'input'}
      />
      {renderIcons()}
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`input-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
          {symbolsCountText && <p className={'input-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  );
};

Input.propTypes = {
  type: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isApiError: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setValue: PropTypes.func,
  subText: PropTypes.string,
  symbolsCountText: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  isRequired: PropTypes.bool,
  autoFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  placeholder: PropTypes.string,
  initialStatus: PropTypes.number,
  Icon: PropTypes.object,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  max: PropTypes.number,
  rows: PropTypes.number,
  setCountryCode: PropTypes.func,
  excludeCountries: PropTypes.array,
  inputRef: PropTypes.object
};

export default Input;
